import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { MENU_OPEN } from '../../../../../store/actions';
import useSidebarCollapsed from '../../useSidebarCollapsed';
import { navButtonSx, navIconSx, navLabelSx, navTextSx } from '../navStyles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isOpen = useSelector((state) => state.customization.isOpen);
  const collapsed = useSidebarCollapsed() && level <= 1;

  const isSelected = !!item && isOpen.findIndex((id) => id === item.id) > -1;

  useEffect(() => {
    if (!item) return;
    const currentIndex = document.location.pathname
      .toString()
      .split('/')
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch({ type: MENU_OPEN, id: item.id });
    }
  }, [pathname]);

  if (!item) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('NavItem rendered without an "item" prop – check the menu config.', { level });
    }
    return null;
  }

  const Icon = item.icon;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.8} size={18} />
  ) : (
    <FiberManualRecordIcon sx={{ width: isSelected ? 8 : 6, height: isSelected ? 8 : 6 }} fontSize="inherit" />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch({ type: MENU_OPEN, id });
  };

  const button = (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      aria-label={collapsed ? item.title : undefined}
      aria-current={isSelected ? 'page' : undefined}
      sx={navButtonSx(theme, { level, collapsed, kind: 'item' })}
      selected={isSelected}
      onClick={() => itemHandler(item.id)}
    >
      <ListItemIcon sx={navIconSx({ level, collapsed })}>{itemIcon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          sx={navTextSx}
          primary={
            <Typography component="span" display="block" sx={navLabelSx(level, isSelected)}>
              {item.title}
            </Typography>
          }
          secondary={
            item.caption && (
              <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block">
                {item.caption}
              </Typography>
            )
          }
        />
      )}
      {!collapsed && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size="small"
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          sx={{ ml: 1, height: 20, fontSize: '0.72rem', fontWeight: 600, '& .MuiChip-label': { px: '7px' } }}
        />
      )}
      {collapsed && item.chip && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 7,
            right: 9,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${theme.palette.background.default}`
          }}
        />
      )}
    </ListItemButton>
  );

  return collapsed ? (
    <Tooltip title={item.title} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;